import { useState } from "react";
import { X, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { BouncingDots } from "./ui/LoadingState";
import { reportIssue, type IssueType, type IssueSeverity } from "../../lib/issues";
import { useAuth } from "../../lib/auth";
import { trackEvent, ANALYTICS_EVENTS } from "../../lib/analytics";

interface BugReportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
    const { user } = useAuth();
    const [type, setType] = useState<IssueType>("other");
    const [severity, setSeverity] = useState<IssueSeverity>("low");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!message.trim()) return;

        setSubmitting(true);
        const { error } = await reportIssue({
            profile_id: user?.id,
            type,
            severity,
            message,
        });

        setSubmitting(false);
        if (!error) {
            trackEvent(ANALYTICS_EVENTS.BUG_REPORT_SUBMITTED, {
                type,
                severity
            });
            setSubmitted(true);
            setTimeout(() => {
                setSubmitted(false);
                setMessage("");
                setType("other");
                setSeverity("low");
                onClose();
            }, 2000);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0D1A12]/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-xl rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-white/20">
                {/* Header with Mesh Gradient */}
                <div className="relative px-8 py-7 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0B6E4F] to-[#065F46] -z-10" />
                    <div className="absolute top-[-50px] right-[-50px] w-40 h-40 bg-[#34D399]/20 rounded-full blur-3xl" />

                    <div className="flex items-center justify-between relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-inner">
                                <AlertCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-extrabold text-white tracking-tight">Report a Bug</h2>
                                <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest mt-0.5">Performance & Reliability</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all text-white border border-white/10"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {submitted ? (
                    <div className="p-12 text-center animate-in zoom-in-95 duration-500">
                        <div className="w-20 h-20 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-[#D1FAE5]">
                            <CheckCircle2 className="w-10 h-10 text-[#0B6E4F]" />
                        </div>
                        <h3 className="text-2xl font-black text-[#0D1A12] mb-2 tracking-tight">Report Received!</h3>
                        <p className="text-[#6B7280] text-sm leading-relaxed">
                            Our engineers have been pinged. We appreciate you helping us make <span className="text-[#0B6E4F] font-bold">MoniFlow</span> better!
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-8 space-y-6">
                        <div>
                            <label className="block text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2 ml-1">
                                Problem Type
                            </label>
                            <div className="relative">
                                <select
                                    value={type}
                                    onChange={(e) => setType(e.target.value as IssueType)}
                                    className="w-full bg-[#F9FAFB] border border-[#E5E7EB] rounded-2xl px-5 py-4 text-sm text-[#111111] font-semibold focus:border-[#0B6E4F] focus:outline-none focus:ring-4 focus:ring-[#0B6E4F]/5 transition-all cursor-pointer appearance-none"
                                >
                                    <option value="ui_error">Something looks wrong (UI)</option>
                                    <option value="api_error">Data not loading (API)</option>
                                    <option value="payment_failure">Payment issue</option>
                                    <option value="performance">App feels slow</option>
                                    <option value="other">Other issue</option>
                                </select>
                                <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-[#9CA3AF]">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2 ml-1">
                                Severity Level
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {(['low', 'medium', 'high', 'critical'] as IssueSeverity[]).map((s) => {
                                    const isActive = severity === s;
                                    const isHigh = s === 'critical' || s === 'high';
                                    return (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setSeverity(s)}
                                            className={`px-4 py-2.5 rounded-xl text-xs font-extrabold capitalize border-2 transition-all duration-200 ${isActive
                                                ? isHigh
                                                    ? 'bg-[#FEF2F2] border-[#EF4444] text-[#B91C1C] shadow-lg shadow-red-500/10'
                                                    : 'bg-[#F0FDF4] border-[#10B919] text-[#065F46] shadow-lg shadow-green-500/10'
                                                : 'bg-white border-[#F3F4F6] text-[#9CA3AF] hover:border-[#E5E7EB] hover:text-[#374151]'
                                                }`}
                                        >
                                            <div className="flex items-center justify-center gap-2">
                                                {isActive && <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${isHigh ? 'bg-red-500' : 'bg-green-500'}`} />}
                                                {s}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2 ml-1">
                                Details
                            </label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Tell us exactly what went wrong..."
                                rows={4}
                                required
                                className="w-full bg-[#F9FAFB] border border-[#E5E7EB] rounded-2xl px-5 py-4 text-sm text-[#111111] placeholder:text-[#D1D5DB] focus:border-[#0B6E4F] focus:outline-none focus:ring-4 focus:ring-[#0B6E4F]/5 transition-all resize-none leading-relaxed"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting || !message.trim()}
                            className="w-full flex items-center justify-center gap-3 bg-[#0B6E4F] hover:bg-[#065F46] text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-xl shadow-[#0B6E4F]/20 disabled:opacity-50 disabled:cursor-not-allowed group active:scale-[0.98]"
                        >
                            {submitting ? (
                                <BouncingDots color="white" />
                            ) : (
                                <>
                                    <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    <span>Send Report</span>
                                </>
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
